/**
 * Витрина честности: пришло → сняли и по каким правилам → осталось.
 *
 * Одна полоса, которая отвечает на вопрос, задаваемый первым и не имеющий
 * ответа ни у одного сканера на рынке: «а всё ли вы мне показываете?».
 *
 * Почему это не украшение. Конкурент пишет «найдено 12 216 788» и не говорит
 * ни слова о вычитании — число означает ровно то, что фильтров у него нет.
 * Короткая лента при этом выглядит одинаково в двух совершенно разных случаях:
 * рынок спокоен, или мы срезали девять десятых по своим правилам. Человек,
 * который не может их различить, уходит к тому, у кого «миллион».
 *
 * Три принципа разметки, каждый оплачен разбором чужих экранов.
 *
 * **Приход называется первым.** «Убрали 40» без прихода читается и как «пришло
 * 45», и как «пришло 4000». Проценты считаются от прихода, а не от остатка.
 *
 * **Причины зовутся словами, а не кодами.** `belowThreshold` человек прочитать
 * не может; «не дотянули до вашего порога процента» — может, и сразу видит,
 * что это ЕГО ручка, а не наш каприз.
 *
 * **Неизвестное не превращается в ноль.** Нет канала — нет полосы. Ноль в
 * графе прихода утверждал бы, что донор молчит, а это отдельная авария со
 * своим сторожем.
 */

import { useState } from 'react';

import type { Funnel, Meta } from './meta';
import { hiddenSince, useScreen } from './store';

/**
 * Имена причин, по которым строка не доехала до ленты.
 *
 * Причины двух родов, и путать их нельзя. Первые — НАШ отбор (порог процента,
 * возраст): человек может их отменить и должен об этом знать. Вторые —
 * состояние данных (не прочиталась, свернулась с такой же): отменять нечего,
 * но молчать тоже нельзя.
 */
const CLASS_NAME: Record<string, string> = {
  arithmetic: 'арифметика — данных не требует',
  immaturity: 'наши часы и оговорки, не мёртвая цена',
  market: 'догадка рынка — свидетель не согласился',
};

const REASON: Record<string, string> = {
  belowThreshold: 'не дотянули до порога процента',
  tooOld: 'приехали уже старыми',
  dropped: 'не прочитались: строку донора не удалось разобрать',
  collapsed: 'свернулись с такой же вилкой того же матча',
  bannedSport: 'дисциплина, где ставку не принять (настольный теннис)',
  staleQuote: 'цена не двигалась часами, вилки, скорее всего, уже нет',
  hiddenByRule: 'сняты проверками',
};

const plural = (n: number, one: string, few: string, many: string): string => {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 > 10 && mod100 < 20) return many;
  if (mod10 === 1) return one;
  if (mod10 > 1 && mod10 < 5) return few;
  return many;
};

function OwnFunnel({ funnel, open }: { funnel: Funnel; open: boolean }): React.ReactElement {
  const found = funnel.found;
  const passed = funnel.passedGate;
  const capped = funnel.snapshotCapped;
  const clones = funnel.clonesRejected;
  const byClass = Object.entries(funnel.hiddenByClass)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);
  return (
    <div className="nx-hon__own">
      <ul>
        <li>
          <span>найдено после матчинга</span>
          <b>{found === null ? '—' : found}</b>
        </li>
        <li>
          <span>прошло гейт публикации</span>
          <b>{passed === null ? '—' : passed}</b>
        </li>
        <li>
          <span>влезло в кадр</span>
          <b>{capped === null ? '—' : capped}</b>
        </li>
        {clones !== null && clones > 0 && (
          <li>
            <span>пар отсечено как клоны — вилка не рождалась</span>
            <b>−{clones}</b>
          </li>
        )}
      </ul>
      {open && byClass.length > 0 && (
        <div className="nx-hon__rules">
          {byClass.map(([code, count]) => (
            <span key={code}>
              {CLASS_NAME[code] ?? code} <b>{count}</b>
            </span>
          ))}
        </div>
      )}
      {open && (
        <p className="nx-hon__note">
          протухшее среди показанных — наши часы, не скрытие. прятать stale нельзя:
          замер 13.08 убил бы живые рабочие строки.
        </p>
      )}
    </div>
  );
}

export function Honest({ meta }: { meta: Meta }): React.ReactElement | null {
  const baseline = useScreen((s) => s.baseline);
  const [open, setOpen] = useState(false);

  const intake = meta.intake;
  const funnel = meta.funnel;
  const hasIntake = intake !== null && intake.got > 0;
  const hasOwn = funnel !== null && (funnel.passedGate !== null || funnel.found !== null);
  if (!hasIntake && !hasOwn) return null;

  // Проверками снятое приходит отдельным счётчиком, монотонным с запуска
  // процесса: клиент вычитает то, что запомнил на подключении, и получает своё
  // окно без серверных сессий.
  const byRule = hiddenSince(meta.hidden, baseline);
  const ruleTotal = Object.values(byRule).reduce((sum, one) => sum + one, 0);

  const share = (n: number): string =>
    intake === null || intake.got === 0 ? '' : `${Math.round((n * 100) / intake.got)} %`;
  const reasons = Object.entries(intake?.reasons ?? {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);
  const headFrom = hasIntake ? intake.got : (funnel?.found ?? funnel?.passedGate ?? '—');
  const headTo = hasIntake ? intake.kept : (funnel?.snapshotCapped ?? funnel?.passedGate ?? '—');

  return (
    <div className={`nx-hon ${open ? 'open' : ''}`}>
      <button type="button" className="nx-hon__head" onClick={() => setOpen(!open)}>
        <span className="nx-lbl">{hasIntake ? 'что пришло и что осталось' : 'своя воронка'}</span>
        <b>{headFrom}</b>
        <em>→</em>
        <b>{headTo}</b>
        <span className="nx-hon__say">
          {hasIntake
            ? intake.got === intake.kept
              ? 'ничего не снято'
              : `снято ${intake.got - intake.kept} ${plural(
                  intake.got - intake.kept,
                  'строка',
                  'строки',
                  'строк',
                )}`
            : 'факт, догадка и клоны — разные числа'}
        </span>
        <span className="nx-hon__more">{open ? 'свернуть' : 'по каким правилам'}</span>
      </button>

      {open && (
        <div className="nx-hon__body">
          {hasOwn && funnel !== null && <OwnFunnel funnel={funnel} open />}
          {hasIntake && intake !== null && (
            <>
              <ul>
                <li>
                  <span>прислал донор в этом такте</span>
                  <b>{intake.got}</b>
                </li>
                {reasons.map(([code, count]) => (
                  <li key={code}>
                    <span>{REASON[code] ?? code}</span>
                    <b>
                      −{count} <i>{share(count)}</i>
                    </b>
                  </li>
                ))}
                {ruleTotal > 0 && (
                  <li>
                    <span>сняли проверки с тех пор, как открыта вкладка</span>
                    <b>−{ruleTotal}</b>
                  </li>
                )}
                <li className="nx-hon__left">
                  <span>осталось в ленте</span>
                  <b>
                    {intake.kept} <i>{share(intake.kept)}</i>
                  </b>
                </li>
              </ul>
              {ruleTotal > 0 && (
                <div className="nx-hon__rules">
                  {Object.entries(byRule)
                    .sort((a, b) => b[1] - a[1])
                    .map(([code, count]) => (
                      <span key={code}>
                        {meta.hiddenGroups[code] ?? code} <b>{count}</b>
                      </span>
                    ))}
                </div>
              )}
              <p className="nx-hon__note">
                приход и первые причины считаются за последнюю выдачу донора, а снятое проверками
                с того момента, как открыта эта вкладка. в одно окно это не сложить: донор
                присылает выдачу целиком, а проверки работают потоком.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
